/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */
import { LogLevel } from "@azure/msal-browser";

/**
 * Configuration object to be passed to MSAL instance on creation. 
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md 
 */

const _baseHref: string = process.env.PUBLIC_URL || "";

let _redirect_uri: string = (window.location.origin.toString() || window.location.href.toString());
if (_redirect_uri.endsWith('/')) {
  _redirect_uri = window.location.href.toString().slice(0, -1);
}
_redirect_uri = _redirect_uri  + _baseHref;

const _clientId: string = process.env.REACT_APP_CLIENT_ID || "";
const _tenantId: string = process.env.REACT_APP_TENANT_ID || "";
const _authority: string = process.env.REACT_APP_AUTHORITY || "";

const _graphMeEndpoint: string = process.env.REACT_APP_ME_ENDPOINT || "";
const _graphPhotoEndpoint: string = process.env.REACT_APP_PHOTO_ENDPOINT || "";
const _graphUsersEndpoint: string = process.env.REACT_APP_USERS_ENDPOINT || "";

const _scopes: string = process.env.REACT_APP_SCOPES || "";

export const msalConfig = {
    auth: {
        clientId: _clientId,
        authority: `${_authority}${_tenantId}`,
        redirectUri: _redirect_uri,
    },
    cache: {
        cacheLocation: "sessionStorage", // This configures where your cache will be stored
        storeAuthStateInCookie: true, // Set this to "true" if you are having issues on IE11 or Edge
    },
    system: {
        loggerOptions: {
            loggerCallback: (level: any, message: any, containsPii: any) => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case LogLevel.Error:
                        console.error(message);
                        return;
                    case LogLevel.Info:
                        console.info(message);
                        return;
                    case LogLevel.Verbose:
                        console.debug(message);
                        return;
                    case LogLevel.Warning:
                        console.warn(message);
                        return;
                    default:
                        return;
                }
            },
            piiLoggingEnabled: false,
        },
        windowHashTimeout: 60000,
        iframeHashTimeout: 6000,
        loadFrameTimeout: 0,
    }
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit: 
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
    // scopes: ["user.read", "profile", "email", "openid"]
    scopes: [..._scopes.split(",")]
};

/**
 * Add here the scopes to request when obtaining an access token for MS Graph API. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const graphConfig = {
    graphMeEndpoint: _graphMeEndpoint,
    graphPhotoEndpoint: _graphPhotoEndpoint,
    graphUsersEndpoint: _graphUsersEndpoint,
};
